/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useCallback, useContext, useMemo, useState, useRef } from 'react';
import { extend, get, contains, isEmpty, isEqual, map, omit } from 'underscore';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import {
  Collapse,
  TextTitle,
  useCollapseState,
  ValidatedFixedPanel,
  ValidatedPanel,
  ValidatedBootstrapPanel,
  getPanelErrorProps,
  collapseExpander,
} from '@bingads-webui-campaign/wizard-jumbo-card';
import { Link, Stack } from '@bingads-webui-react/fabric-control';
import { Panel as BootstrapPanel } from 'react-bootstrap';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { AdGroup } from '@bingads-webui/campaign-odata-schemas';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { ValidationGroup, ErrorMessages } from '@bingads-webui-react/validation';
import { ActuationGroup } from '@bingads-webui-campaign-react/actuator';
import { AttentionBeaconGroup } from '@bingads-webui-campaign-react/attention-beacon';
import { FormErrorGroup, UnmappedFormErrors, andComp, orComp, entityIs, propertyIs, anyComp } from '@bingads-webui-campaign-react/form-error';
import { withControlErrorHandling, withUnvalidatedControlErrors } from '@bingads-webui-campaign-react/error-handling';
import {
  getBidAdjuestmentTitleDetails,
  getBidAdjuestmentTitleSummary,
  getLocationTargetTitleDetails,
  ValidatedStandardAdGroup,
  ValidatedBidAdjustment,
  ValidatedLocationTargetSetting,
  withUseCampaignSettings,
} from '@bingads-webui-campaign-react/search-campaign-wizard-controls';
import { useFormService } from '@bingads-webui-react/form';
import { ValidationStringMap } from '@bingads-webui/validation-string-map';
import { buildLegacyI18n } from '@bingads-webui/build-legacy-i18n';
import { flattenTargetCriterion, loadBundleCountries, updateAdGroupAgeTargets, ERROR_ENTITY } from '@bingads-webui-campaign-react/search-campaign-data';
import { ValidatedDefaultBid, isAutoBidding } from '@bingads-webui-campaign-react/default-bid';
import { formatBidValue } from '@bingads-webui-campaign-react/summary-page';
import { WebExclusionsSection } from '@bingads-webui-react/web-exclusions';
import { ScopeLevel } from '@bingads-webui-campaign-react/constants';
import { AdDistributionWithValidationSection } from '@bingads-webui-campaign/ad-distribution';
import { SingleLanguageTargetPanel } from '@bingads-webui-campaign-react/wizard-shared-components';
import { MultimediaAdBidAdjustmentSection } from '@bingads-webui-campaign-react/multimedia-ad-adjustment';
import { ValidatedDateSetting, getDateSettingDetails } from '@bingads-webui-campaign-react/date-setting';
import { ValidatedLegacyAdSchedule, getAdScheduleDetails } from '@bingads-webui-campaign/ad-schedule';
import { URLOptionsSection } from '@bingads-webui-campaign-react/validated-url-input';
import { AdRotationSection } from '@bingads-webui-react/ad-rotation';
import { testUurl, uurlLevels } from '@bingads-webui-component-legacy/upgraded-url/service/testUurl';
import {
  ValidatedCompanyPicker,
  ValidatedIndustryPicker,
  ValidatedJobFunctionPicker,
} from '@bingads-webui-campaign-react/display-plus-campaigns';

import { getStyles } from './styles';
import { AD_GROUP_TYPES, AD_GROUP_TYPE_STRINGS } from '../step-utils';
import BidImg from '../../shared/media/bid.svg';
import AdDistributionImg from '../../shared/media/ad-distribution.svg';
import DateSettingImg from '../../shared/media/date-setting.svg';
import AdScheduleImg from '../../shared/media/ad-schedule.svg';
import DeviceImg from '../../shared/media/device.svg';
import GenderImg from '../../shared/media/gender.svg';
import AgeImg from '../../shared/media/age.svg';
import LocationImg from '../../shared//media/location.svg';
import CompanyNameImg from '../../shared/media/company-name.svg';
import IndustryImg from '../../shared/media/industry.svg';
import JobFunctionImg from '../../shared/media/job-function.svg';

const adgroupLocationCriterions = anyComp(
  entityIs(ERROR_ENTITY.AdGroupLocation),
  entityIs(ERROR_ENTITY.AdGroupRadius),
  entityIs(ERROR_ENTITY.AdGroupNegativeLocation),
  entityIs(ERROR_ENTITY.AdGroupLocationIntent)
);
const entityIsAdGroup = entityIs(ERROR_ENTITY.AdGroup);
const ServerValidatedDefaultBid = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedDefaultBid, { filter: andComp(entityIsAdGroup, propertyIs('BidAmount')) }));
const ServerValidatedLegacyDateSetting = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedDateSetting, { filter: andComp(entityIsAdGroup, orComp(propertyIs('StartDate'), propertyIs('EndDate'))) }));
const ServerValidatedLegacyAdSchedule = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedLegacyAdSchedule, { filter: entityIs(ERROR_ENTITY.AdGroupDayTimeCriterion) }));
const ServerValidatedDevice = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedBidAdjustment, { filter: entityIs(ERROR_ENTITY.AdGroupDeviceOSCriterion) }));
const ServerValidatedGender = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedBidAdjustment, { filter: entityIs(ERROR_ENTITY.AdGroupGenderCriterion) }));
const ServerValidatedAge = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedBidAdjustment, { filter: entityIs(ERROR_ENTITY.AdGroupAgeCriterion) }));
const ServerValidatedLocationTargetSetting = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedLocationTargetSetting, { filter: adgroupLocationCriterions }));
const ServerValidatedCompanyName = withControlErrorHandling(withUnvalidatedControlErrors(withUseCampaignSettings(ValidatedCompanyPicker), { filter: entityIs(ERROR_ENTITY.AdGroupCompanyNameCriterion) }));
const ServerValidatedIndustry = withControlErrorHandling(withUnvalidatedControlErrors(withUseCampaignSettings(ValidatedIndustryPicker), { filter: entityIs(ERROR_ENTITY.AdGroupIndustryCriterion) }));
const ServerValidatedJobFunction = withControlErrorHandling(withUnvalidatedControlErrors(withUseCampaignSettings(ValidatedJobFunctionPicker), { filter: entityIs(ERROR_ENTITY.AdGroupJobFunctionCriterion) }));

export const AdGroupEditSettingsStep = ({
  data,
  onDataChange,
  error,
}) => {
  const {
    serverErrors = [],
    systemErrors = [],
  } = error || {};

  const i18n = useContext(I18nContext);
  const legacyI18n = useMemo(() => buildLegacyI18n(i18n, [ValidationStringMap]), [i18n]);
  const classes = useDefaultStyles(getStyles);
  const serverContext = useContext(ServerContext);
  const {
    DynamicPermissions,
    Permissions,
    Token,
    CurrentUser: { Market: market },
  } = serverContext;
  const permissions = useMemo(() => ({ dynamic: { ...DynamicPermissions }, ...Permissions }), [DynamicPermissions, Permissions]);
  const { scope } = useContext(ScopeContext);
  const {
    cid, aid, campaignId, uid, adGroupId,
  } = scope.values;
  const refreshLocationTargetsTimes = useRef(0);
  const {
    adGroup,
    campaign,
  } = data || {};
  const selectedLanguages = useRef(adGroup && adGroup.Languages);
  selectedLanguages.current = adGroup && adGroup.Languages === 'None' ? campaign && campaign.Languages : adGroup && adGroup.Languages;

  const {
    TrackingUrlTemplate, FinalUrlSuffix, UrlCustomParameters,
  } = adGroup || {};

  const { IsDSAMixedModeCampaignEnabled } = permissions || {};

  const isExperiment = !!(campaign && campaign.ExperimentId);
  const isMixedMode = IsDSAMixedModeCampaignEnabled && !isExperiment;

  const urlOptions = useMemo(() => ({
    TrackingUrlTemplate,
    FinalUrlSuffix,
    UrlCustomParameters,
  }), [FinalUrlSuffix, TrackingUrlTemplate, UrlCustomParameters]);

  const urlOptionsDetails = useRef(urlOptions);
  urlOptionsDetails.current = urlOptions;

  const { state: { context: { saveFailed: showAllValidationErrors } } } = useFormService();
  const [adScheduleView, setAdScheduleView] = useState(null);
  const [advancedSettingsSectionExpanded, setAdvancedSettingsSectionExpanded] = useState(false);
  const onAdvancedSettingsSectionToggle = useCallback((expanded) => {
    setAdvancedSettingsSectionExpanded(expanded);
  }, []);

  const [
    allSettingsSections,
    settingsExpandedSections,
    setSettingsExpandedSections,
  ] = useCollapseState(
    ['adGroup', 'language', 'bid'] /* all sections */,
    ['adGroup', 'language', 'bid'] /* initially expanded sections */
  );

  const basicSettingsExpand = useMemo(
    () => collapseExpander({ setActiveKeys: setSettingsExpandedSections }),
    [setSettingsExpandedSections]
  );
  const [
    allBidAdjustmentSections,
    bidAdjustmentExpandedSections,
    setBidAdjustmentExpandedSections,
    flipBidAdjustmentExpandAllState,
    expandAllBidAdjustmentSections,
  ] = useCollapseState(
    ['locations', 'device', 'gender', 'age', 'companyName', 'industry', 'jobFunction'] /* all sections */,
    [] /* initially expanded sections */
  );

  const allBidAdjustmentExpand = useMemo(
    () => collapseExpander({ setActiveKeys: setBidAdjustmentExpandedSections }),
    [setBidAdjustmentExpandedSections]
  );

  const [bidAdjustmentSectionExpanded, setBidAdjustmentSectionExpanded] = useState(false);
  const onBidAdjustmentSectionToggle = useCallback((expanded) => {
    setBidAdjustmentSectionExpanded(expanded);
  }, []);

  const [
    allAdvancedSections,
    advancedExpandedSections,
    setAdvancedExpandedSections,
    flipAdvancedExpandAllState,
    expandAllAdvancedSettingsSections,
  ] = useCollapseState(
    ['ad-schedule', 'start-end-date', 'ad-distribution', 'web-exclusions', 'advanced-url-options', 'multimedia-ad', 'ad-rotation'] /* all sections */,
    ['ad-schedule', 'start-end-date', 'web-exclusions'] /* initially expanded sections */
  );

  const onValidate = useCallback((result) => {
    if (data && data.adGroup) {
      let { Age } = result.data;
      if (result.data.ageRanges && !isEqual(result.data.ageRanges, data.adGroup.ageRanges)) {
        Age = updateAdGroupAgeTargets(result.data.Age || data.adGroup.Age, result.data.ageRanges);
      }
      const newLanguages = result && result.data && result.data.Languages === 'None' ? data.campaign.Languages : result.data.Languages;
      if (!isEmpty(newLanguages) && selectedLanguages.current !== newLanguages) {
        selectedLanguages.current = newLanguages;
        refreshLocationTargetsTimes.current = (refreshLocationTargetsTimes.current + 1) % 1000;
        loadBundleCountries({
          cid, aid, serverContext, language: newLanguages,
        }).then((bundleCountriesAndLocationCriterions) => {
          onDataChange({
            data: {
              ...data,
              adGroup: {
                ...data.adGroup,
                ...result.data,
                ...(Age && { Age }),
              },
              ...bundleCountriesAndLocationCriterions,
            },
            isValid: result.isValid,
          });
        });
      } else {
        onDataChange({
          data: {
            ...data,
            adGroup: {
              ...data.adGroup,
              ...result.data,
              ...(Age && { Age }),
            },
          },
          isValid: result.isValid,
        });
      }
    }
  }, [aid, cid, data, onDataChange, serverContext]);

  const webExclusionsError = serverErrors.filter(err => err.property === 'webExclusions');
  const setExclusionsCallback = useCallback((exclusionsData, type) => {
    if (data) {
      const patchedData = {
        data: {
          ...data,
          exclusions: {
            ...(data.exclusions || {}),
            adgroupExclusions: {
              ...(data.exclusions && data.exclusions.adgroupExclusions ? data.exclusions.adgroupExclusions : {}),
              [type]: exclusionsData,
            },
          },
        },
      };
      onDataChange(patchedData);
    }
  }, [data, onDataChange]);

  const triggerTestUurl = useCallback((fn, originalView) => {
    extend(originalView, {
      isInline: false,
      level: uurlLevels.Campaign,
    });
    const context = {
      customerId: cid,
      userId: uid,
      accountId: aid,
      campaignId,
      adGroupId,
    };
    const params = urlOptionsDetails.current;
    const options = {
      apiInLog: 'advancedSettingsUrlOptions/uurlTest',
    };
    testUurl(fn, originalView, params, context, options);
  }, [adGroupId, aid, campaignId, cid, uid]);

  const basicSettingsSection = useMemo(() => {
    const adGroupPanel = (
      <ValidatedFixedPanel
        key="adGroup"
        title={<TextTitle titleKey={_TL_('Ad group')} />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      >
        <ValidatedStandardAdGroup
          adGroup={adGroup}
          currentAdGroupType={adGroup && adGroup.AdGroupType}
          onSwitchAdGroupType={() => { }}
          adGroupTypes={AD_GROUP_TYPES}
          adGroupTypeStrings={AD_GROUP_TYPE_STRINGS}
          isMixedMode={isMixedMode}
          isEditingScenario
        />
      </ValidatedFixedPanel>
    );

    const languagePanel = (
      adGroup && adGroup.AdGroupType && adGroup.AdGroupType === AD_GROUP_TYPES.SearchStandard &&
      <SingleLanguageTargetPanel
        data={adGroup}
        expand={basicSettingsExpand}
        key="language"
      />
    );

    const adgroupBidStrategy = data && data.adGroup && data.adGroup.BiddingScheme && data.adGroup.BiddingScheme.Type;
    const campaignBidStrategy = data && data.campaign && data.campaign.BiddingScheme && data.campaign.BiddingScheme.Type;
    const disableBid = adgroupBidStrategy && adgroupBidStrategy === 'InheritFromParent' && isAutoBidding(campaignBidStrategy);

    const bidPanel = (
      <ValidatedPanel
        key="bid"
        title={<TextTitle titleKey={_TL_('Default bid')} icon={BidImg} topicKey="pop_BA_Apps_Bid" details={disableBid ? '' : formatBidValue(i18n, data && data.adGroup && data.adGroup.SearchBid, serverContext.CurrentAccount.CurrencyCode)} />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
        actuationProps={{
          expand: basicSettingsExpand,
          expandKey: 'bid',
        }}
      >
        <ServerValidatedDefaultBid
          Type={adgroupBidStrategy === 'InheritFromParent' ? campaignBidStrategy : adgroupBidStrategy}
          i18n={i18n}
          field="SearchBid"
          id="default-bid"
          label={i18n.getString(_TL_('Bid'))}
          value={data && data.adGroup && data.adGroup.SearchBid}
          onValidate={onValidate}
          disabled={disableBid}
        />
      </ValidatedPanel>
    );

    const basicSettings = [
      adGroupPanel,
      languagePanel,
      bidPanel,
    ];

    return (
      <div className={classNames('basic-adgroup-section')}>
        <Collapse allPanelItems={allSettingsSections} checkItems skipItemsTypeCheck activeKeys={settingsExpandedSections} setActiveKeys={setSettingsExpandedSections}>
          {basicSettings}
        </Collapse>
      </div>
    );
  }, [adGroup, isMixedMode, basicSettingsExpand, data, i18n, serverContext.CurrentAccount.CurrencyCode, onValidate, allSettingsSections, settingsExpandedSections, setSettingsExpandedSections]);

  const bidAdjustmentSection = useMemo(() => {
    const locationPanel = (
      data && data.adGroup &&
      <ValidatedPanel
        key="locations"
        noOverflowHidden
        title={<TextTitle
          titleKey={_TL_('Location')}
          topicKey="pop_BA_CampaignCreate_Settings_LocationTargeting"
          details={
            getLocationTargetTitleDetails(
              i18n,
              data.adGroup.locationTargets,
              data && data.bundleCountries,
              true,
              campaign && campaign.BiddingScheme && campaign.BiddingScheme.Type,
              true
            )}
          icon={LocationImg}
        />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
        actuationProps={{
          expand: basicSettingsExpand,
          expandKey: 'locations',
        }}
      >
        <ServerValidatedLocationTargetSetting
          validationProps={{
            data: adGroup && adGroup.locationTargets,
          }}
          bundleCountries={data && data.bundleCountries}
          bundleLocationCriterions={data && data.bundleLocationCriterions}
          refreshLocationTargetsTimes={refreshLocationTargetsTimes.current}
          // eslint-disable-next-line no-nested-ternary
          language={adGroup && adGroup.language ? (adGroup.language === 'None' ? campaign && campaign.Languages : [adGroup && adGroup.language]) : []}
          market={market}
          isAdGroupLevel
          showUserParentLevelToggle
          permissions={permissions}
          scope={scope}
          bidStrategyType={campaign && campaign.DisplayBiddingStrategyType}
          allowBidAdjustment
          keepInitValueWhenToggle
        />
      </ValidatedPanel>
    );
    const devicePanel = (
      data && data.adGroup &&
      <ValidatedPanel
        key="device"
        noOverflowHidden
        title={<TextTitle
          titleKey={_TL_('Device')}
          icon={DeviceImg}
          topicKey="pop_BA_CampaignCreate_AudTargets_Device"
          details={getBidAdjuestmentTitleDetails(i18n, !permissions.IsOriginalDeviceTargetingEnabled && (data.adGroup.currentSelectedDeviceLevel === 'adgroup' ? data.adGroup.Device : data.campaign && data.campaign.Device), 'Device', data.adGroup.currentSelectedDeviceLevel === 'campaign')}
        />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      >
        <ServerValidatedDevice
          cid={cid}
          aid={aid}
          campaignId={campaignId}
          adGroupId={adGroupId}
          criterionType="Device"
          criterionLevel="AdGroupCriterion"
          level="adgroup"
          i18n={i18n}
          data={data.adGroup.Device}
          parentData={data.campaign && data.campaign.Device}
          readOnly={data.adGroup.currentSelectedDeviceLevel !== 'adgroup'}
        />
      </ValidatedPanel>
    );

    const genderPanel = (
      data && data.adGroup &&
      <ValidatedPanel
        key="gender"
        noOverflowHidden
        title={<TextTitle
          titleKey={_TL_('Gender')}
          icon={GenderImg}
          topicKey="pop_BA_CampaignCreate_AudTargets_Gender"
          details={getBidAdjuestmentTitleDetails(i18n, data.adGroup.currentSelectedGenderLevel === 'adgroup' ? data.adGroup.Gender : data.campaign && data.campaign.Gender, 'Gender', data.adGroup.currentSelectedGenderLevel === 'campaign')}
        />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      >
        <ServerValidatedGender
          cid={cid}
          aid={aid}
          campaignId={campaignId}
          adGroupId={adGroupId}
          criterionType="Gender"
          criterionLevel="AdGroupCriterion"
          level="adgroup"
          i18n={i18n}
          data={data.adGroup.Gender}
          parentData={data.campaign && data.campaign.Gender}
          readOnly={data.adGroup.currentSelectedGenderLevel !== 'adgroup'}
        />
      </ValidatedPanel>
    );

    const agePanel = (
      data && data.adGroup &&
      <ValidatedPanel
        key="age"
        noOverflowHidden
        title={<TextTitle
          titleKey={_TL_('Age')}
          icon={AgeImg}
          topicKey="pop_BA_CampaignCreate_AudTargets_Age"
          details={getBidAdjuestmentTitleDetails(i18n, data.adGroup.currentSelectedAgeLevel === 'adgroup' ? data.adGroup.Age : data.campaign && data.campaign.Age, 'Age', data.adGroup.currentSelectedAgeLevel === 'campaign')}
        />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      >
        <ServerValidatedAge
          cid={cid}
          aid={aid}
          campaignId={campaignId}
          adGroupId={adGroupId}
          criterionType="Age"
          criterionLevel="AdGroupCriterion"
          level="adgroup"
          i18n={i18n}
          data={data.adGroup.Age}
          ranges={data.adGroup.ageRanges}
          parentData={data.campaign && data.campaign.Age}
          readOnly={data.adGroup.currentSelectedAgeLevel !== 'adgroup'}
        />
      </ValidatedPanel>
    );

    // For campaign under Pilot 1183 DomesticSimplifiedChinese, companyName, industry, jobFunction is not applicable
    const companyNamePanel = permissions && !permissions.IsDomesticSimplifiedChineseEnabled && adGroup && (
      <ValidatedPanel
        key="companyName"
        noOverflowHidden
        title={<TextTitle
          titleKey={_TL_('Company')}
          icon={CompanyNameImg}
          topicKey="pop_BA_CampaignCreate_AudTargets_Company"
          details={getBidAdjuestmentTitleSummary(i18n, adGroup.companyNameTargets, {
            singleTarget: _TL_('Targeted: {{number}} company'),
            multipleTargets: _TL_('Targeted: {{number}} companies'),
          }, adGroup.currentSelectedCompanyNameLevel === 'campaign')}
        />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      >
        <ServerValidatedCompanyName
          classes={classes}
          i18n={i18n}
          showBidAdjustments
          showExclusion={false}
          level={adGroup.currentSelectedCompanyNameLevel}
          onLevelChange={level => onDataChange({
            data: {
              ...data,
              adGroup: {
                ...adGroup,
                ...level === 'campaign' && {
                  companyNameTargets: campaign.companyNameTargets,
                  adGroupCompanyNameTargets: adGroup.companyNameTargets,
                },
                ...level === 'adgroup' && {
                  companyNameTargets: isEmpty(adGroup.adGroupCompanyNameTargets) ? map(campaign.companyNameTargets, target => omit(target, 'Id')) : adGroup.adGroupCompanyNameTargets,
                },
                currentSelectedCompanyNameLevel: level,
              },
            },
          })}
          validationProps={{
            data: {
              companyNameTargets: adGroup.companyNameTargets,
            },
          }}
        />
      </ValidatedPanel>
    );

    const industryPanel = permissions && !permissions.IsDomesticSimplifiedChineseEnabled && adGroup && (
      <ValidatedPanel
        key="industry"
        noOverflowHidden
        title={<TextTitle
          titleKey={_TL_('Industry')}
          icon={IndustryImg}
          topicKey="pop_BA_CampaignCreate_AudTargets_Industry"
          details={getBidAdjuestmentTitleSummary(i18n, adGroup.industry, {
            singleTarget: _TL_('Targeted: {{number}} industry'),
            multipleTargets: _TL_('Targeted: {{number}} industries'),
          }, adGroup.currentSelectedIndustryLevel === 'campaign')}
        />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      >
        <ServerValidatedIndustry
          classes={classes}
          i18n={i18n}
          showBidAdjustments
          showExclusion={false}
          level={adGroup.currentSelectedIndustryLevel}
          onLevelChange={level => onDataChange({
            data: {
              ...data,
              adGroup: {
                ...adGroup,
                ...level === 'campaign' && {
                  industry: campaign.industry,
                  initialIndustryTargets: campaign.initialIndustryTargets,
                  adGroupIndustry: adGroup.industry,
                  adGroupInitialIndustryTargets: flattenTargetCriterion(adGroup.industryTargetsBidAdjustment, 'Industry'),
                },
                ...level === 'adgroup' && {
                  industry: isEmpty(adGroup.adGroupIndustry) ? adGroup.industry : adGroup.adGroupIndustry,
                  initialIndustryTargets: isEmpty(adGroup.adGroupIndustry) ? adGroup.initialIndustryTargets : adGroup.adGroupInitialIndustryTargets,
                },
                currentSelectedIndustryLevel: level,
              },
            },
          })}
          validationProps={{
            data: {
              industry: adGroup.industry,
            },
          }}
          initialIndustryTargets={adGroup.initialIndustryTargets}
        />
      </ValidatedPanel>
    );

    const jobFunctionPanel = permissions && !permissions.IsDomesticSimplifiedChineseEnabled && adGroup && (
      <ValidatedPanel
        key="jobFunction"
        noOverflowHidden
        title={<TextTitle
          titleKey={_TL_('Job function')}
          icon={JobFunctionImg}
          topicKey="pop_BA_CampaignCreate_AudTargets_JobFunction"
          details={getBidAdjuestmentTitleSummary(i18n, adGroup.jobTargets, {
            singleTarget: _TL_('Targeted: {{number}} job'),
            multipleTargets: _TL_('Targeted: {{number}} jobs'),
          }, adGroup.currentSelectedJobFunctionLevel === 'campaign')}
        />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      >
        <ServerValidatedJobFunction
          classes={classes}
          i18n={i18n}
          showBidAdjustments
          showExclusion={false}
          level={adGroup.currentSelectedJobFunctionLevel}
          onLevelChange={level => onDataChange({
            data: {
              ...data,
              adGroup: {
                ...adGroup,
                ...level === 'campaign' && {
                  jobTargets: campaign.jobTargets,
                  initialJobFunctionTargets: campaign.initialJobFunctionTargets,
                  adGroupJobTargets: adGroup.jobTargets,
                  adGroupInitialJobFunctionTargets: flattenTargetCriterion(adGroup.jobTargetBidAdjustments, 'JobFunction'),
                },
                ...level === 'adgroup' && {
                  jobTargets: isEmpty(adGroup.adGroupJobTargets) ? adGroup.jobTargets : adGroup.adGroupJobTargets,
                  initialJobFunctionTargets: isEmpty(adGroup.adGroupJobTargets) ? adGroup.initialJobFunctionTargets : adGroup.adGroupInitialJobFunctionTargets,
                },
                currentSelectedJobFunctionLevel: level,
              },
            },
          })}
          validationProps={{
            data: {
              jobTargets: adGroup.jobTargets,
            },
          }}
          initialJobTargets={adGroup.initialJobFunctionTargets}
        />
      </ValidatedPanel>
    );

    const bidAdjustment = [
      locationPanel,
      agePanel,
      devicePanel,
      genderPanel,
      companyNamePanel,
      industryPanel,
      jobFunctionPanel,
    ];

    const onBidAdjustmentSectionCollapse = () => {
      if (!bidAdjustmentSectionExpanded) {
        setBidAdjustmentSectionExpanded(true);
        expandAllBidAdjustmentSections();
      } else {
        flipBidAdjustmentExpandAllState();
      }
    };

    return (
      <div className={classNames('bid-adjustment-section', classes.bidAdjustmentSection)}>
        <ValidatedBootstrapPanel
          onToggle={onBidAdjustmentSectionToggle}
          expanded={bidAdjustmentSectionExpanded}
          actuationProps={{
            expand: allBidAdjustmentExpand,
          }}
          className={classNames(classes.collapsiblePanel, 'bid-adjustment-panel')}
          render={() => (
            <>
              <BootstrapPanel.Heading>
                <BootstrapPanel.Title toggle>
                  {i18n.getString(_TL_('Bid adjustments'))}
                </BootstrapPanel.Title>
                <Stack horizontalAlign="end" tokens={{ padding: '9px 0 0 0' }} className="jumbo-card-control-all-btn-stack">
                  <Link className="jumbo-card-control-all-btn" onClick={onBidAdjustmentSectionCollapse}>{bidAdjustmentSectionExpanded && bidAdjustmentExpandedSections.length ? i18n.getString(_TL_('Collapse all')) : i18n.getString(_TL_('Expand all'))}</Link>
                </Stack>
              </BootstrapPanel.Heading>
              <BootstrapPanel.Collapse>
                <BootstrapPanel.Body>
                  <div className={classNames('bid-adjustment-section')}>
                    <Collapse allPanelItems={allBidAdjustmentSections} checkItems skipItemsTypeCheck activeKeys={bidAdjustmentExpandedSections} setActiveKeys={setBidAdjustmentExpandedSections}>
                      {bidAdjustment}
                    </Collapse>
                  </div>
                </BootstrapPanel.Body>
              </BootstrapPanel.Collapse>
            </>
          )}
        />
      </div>
    );
  }, [adGroup, adGroupId, aid, allBidAdjustmentExpand, allBidAdjustmentSections, basicSettingsExpand, bidAdjustmentExpandedSections, bidAdjustmentSectionExpanded, campaign, campaignId, cid, classes, data, expandAllBidAdjustmentSections, flipBidAdjustmentExpandAllState, i18n, market, onBidAdjustmentSectionToggle, onDataChange, permissions, scope, setBidAdjustmentExpandedSections]);

  const adgroupBiddingScheme = get(adGroup, ['BiddingScheme', 'Type']);
  const campaignBiddingSchema = get(campaign, ['BiddingScheme', 'Type']);
  const disableOptions = (adgroupBiddingScheme === 'InheritFromParent') && contains(['MaxClicks', 'MaxConversions', 'TargetCpa'], campaignBiddingSchema);

  const advancedSettingsSection = useMemo(() => {
    const adScheduleComponent = (
      <ValidatedPanel
        key="ad-schedule"
        noOverflowHidden
        title={<TextTitle titleKey={_TL_('Ad schedule')} topicKey="pop_BA_Targeting_DayTime" icon={AdScheduleImg} details={`${getAdScheduleDetails(i18n, adScheduleView && adScheduleView.current)}`} />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      >
        <ServerValidatedLegacyAdSchedule
          entity={{
            data: {
              TimeZone: data && data.campaign && data.campaign.TimeZone,
              AdScheduleUseSearcherTimeZone: data && data.adGroup && data.adGroup.AdScheduleUseSearcherTimeZone,
              Id: data && data.adGroup && data.adGroup.Id,
            },
          }}
          validationProps={{
            field: 'AdSchedule',
            onValidate,
            data: {
              dayTimeRanges: data && data.adGroup && data.adGroup.dayTimeRanges,
            },
          }}
          setAdScheduleView={setAdScheduleView}
          showBidAdjustmentColumn
          campaignDayTimeRanges={data && data.campaign && data.campaign.dayTimeRanges}
          level="adgroup"
        />
      </ValidatedPanel>
    );

    const dateSettingComponent = (
      <ValidatedPanel
        key="start-end-date"
        noOverflowHidden
        title={<TextTitle titleKey={_TL_('Start and end dates')} icon={DateSettingImg} details={`${getDateSettingDetails(i18n, data && data.adGroup && data.adGroup.StartDate, data && data.adGroup && data.adGroup.EndDate)}`} />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
      >
        <ServerValidatedLegacyDateSetting
          i18n={i18n}
          validationProps={{
            onValidate,
            stopPropagation: true,
            data: {
              StartDate: data && data.adGroup && data.adGroup.StartDate,
              EndDate: data && data.adGroup && data.adGroup.EndDate,
              TimeZone: data && data.campaign && data.campaign.TimeZone,
              isCreate: false,
              status: data && data.adGroup && data.adGroup.Status,
            },
          }}
        />
      </ValidatedPanel>
    );

    const onAdvancedSettingsSectionCollapse = () => {
      if (!advancedSettingsSectionExpanded) {
        setAdvancedSettingsSectionExpanded(true);
        expandAllAdvancedSettingsSections();
      } else {
        flipAdvancedExpandAllState();
      }
    };

    return (
      <div className={classNames('advanced-section')}>
        <ValidatedBootstrapPanel
          onToggle={onAdvancedSettingsSectionToggle}
          expanded={advancedSettingsSectionExpanded}
          className={classNames(classes.collapsiblePanel, 'advanced-settings-panel')}
          render={() => (
            <>
              <BootstrapPanel.Heading>
                <BootstrapPanel.Title toggle>
                  {i18n.getString(_TL_('Advanced settings'))}
                </BootstrapPanel.Title>
                <Stack horizontalAlign="end" tokens={{ padding: '9px 0 0 0' }} className="jumbo-card-control-all-btn-stack">
                  <Link className="jumbo-card-control-all-btn" onClick={onAdvancedSettingsSectionCollapse}>{advancedSettingsSectionExpanded && advancedExpandedSections.length ? i18n.getString(_TL_('Collapse all')) : i18n.getString(_TL_('Expand all'))}</Link>
                </Stack>
              </BootstrapPanel.Heading>
              <BootstrapPanel.Collapse>
                <BootstrapPanel.Body>
                  <div className={classNames('advanced-settings-section')}>
                    <Collapse allPanelItems={allAdvancedSections} checkItems skipItemsTypeCheck activeKeys={advancedExpandedSections} setActiveKeys={setAdvancedExpandedSections}>
                      {adScheduleComponent}
                      {dateSettingComponent}
                      <AdDistributionWithValidationSection
                        data={data && data.adGroup}
                        onValidate={onValidate}
                        i18n={i18n}
                        key="ad-distribution"
                        icon={AdDistributionImg}
                        noOverflowHidden
                      />
                      <WebExclusionsSection
                        key="web-exclusions"
                        showSiteExclusion
                        level={ScopeLevel.AdGroup}
                        exclusionData={data && data.exclusions ? data.exclusions : {}}
                        setExclusionsCallback={setExclusionsCallback}
                        errors={webExclusionsError}
                      />
                      {data && data.adGroup && <URLOptionsSection
                        key="advanced-url-options"
                        trackingUrlTemplate={TrackingUrlTemplate}
                        finalUrlSuffix={FinalUrlSuffix}
                        urlCustomParameters={UrlCustomParameters}
                        token={Token}
                        triggerTestUurl={triggerTestUurl}
                      />
                      }
                      <MultimediaAdBidAdjustmentSection
                        key="multimedia-ad"
                        level={ScopeLevel.AdGroup}
                        data={adGroup}
                        onDataChange={onValidate}
                        expand={setAdvancedExpandedSections}
                        permissions={permissions}
                      />
                      <AdRotationSection
                        key="ad-rotation"
                        data={adGroup}
                        disableOptions={disableOptions}
                      />
                    </Collapse>
                  </div>
                </BootstrapPanel.Body>
              </BootstrapPanel.Collapse>
            </>
          )}
        />
      </div>
    );
  }, [FinalUrlSuffix, permissions, Token, TrackingUrlTemplate, UrlCustomParameters, adGroup, adScheduleView, advancedExpandedSections, advancedSettingsSectionExpanded, allAdvancedSections, classes.collapsiblePanel, data, disableOptions, expandAllAdvancedSettingsSections, flipAdvancedExpandAllState, i18n, onAdvancedSettingsSectionToggle, onValidate, setAdvancedExpandedSections, setExclusionsCallback, triggerTestUurl, webExclusionsError]);

  return (
    <div className={classNames(classes.adGroupEditSettingsStep, 'adgroup-edit-settings-step')}>
      <ActuationGroup actuationProps={{ isActive: showAllValidationErrors }}>
        <AttentionBeaconGroup>
          <FormErrorGroup formErrorProps={{ errors: serverErrors, showAllValidationErrors }}>
            <div className="server-errors">
              <UnmappedFormErrors />
            </div>
            <div className="system-errors">
              <ErrorMessages messages={systemErrors} />
            </div>
            <ValidationGroup
              validationProps={{
                schema: AdGroup,
                onValidate,
                data: data || {},
                i18n: legacyI18n,
              }}
            >
              <div className={classes.formContainer}>
                {basicSettingsSection}
                {bidAdjustmentSection}
                {advancedSettingsSection}
              </div>
            </ValidationGroup>
          </FormErrorGroup>
        </AttentionBeaconGroup>
      </ActuationGroup>
    </div>
  );
};

AdGroupEditSettingsStep.propTypes = {
  data: PropTypes.shape({
    campaign: PropTypes.shape({
      Id: PropTypes.string,
      Name: PropTypes.string,
      Languages: PropTypes.string,
      BiddingScheme: PropTypes.shape({
        Type: PropTypes.string,
      }),
      Age: PropTypes.shape({}),
      Gender: PropTypes.shape({}),
      Device: PropTypes.shape({}),
      companyNameTargets: PropTypes.shape({}),
      industry: PropTypes.shape({}),
      jobTargets: PropTypes.shape({}),
      TimeZone: PropTypes.string,
      dayTimeRanges: PropTypes.arrayOf(PropTypes.shape({})),
    }),
    adGroup: PropTypes.shape({
      Id: PropTypes.string,
      Status: PropTypes.string,
      BiddingScheme: PropTypes.shape({
        Type: PropTypes.string,
      }),
      ageRanges: PropTypes.arrayOf(PropTypes.string),
      Age: PropTypes.shape({}),
      Gender: PropTypes.shape({}),
      Device: PropTypes.shape({}),
      companyNameTargets: PropTypes.shape({}),
      industry: PropTypes.shape({}),
      jobTargets: PropTypes.shape({}),
      locationTargets: PropTypes.shape({}),
      SearchBid: PropTypes.number,
      currentSelectedAgeLevel: PropTypes.string,
      currentSelectedGenderLevel: PropTypes.string,
      currentSelectedDeviceLevel: PropTypes.string,
      currentSelectedCompanyNameLevel: PropTypes.string,
      currentSelectedIndustryLevel: PropTypes.string,
      currentSelectedJobFunctionLevel: PropTypes.string,
      dayTimeRanges: PropTypes.arrayOf(PropTypes.shape({})),
      StartDate: PropTypes.string,
      EndDate: PropTypes.string,
      AdScheduleUseSearcherTimeZone: PropTypes.bool,
    }),
    bundleCountries: PropTypes.arrayOf(PropTypes.shape({})),
    bundleLocationCriterions: PropTypes.arrayOf(PropTypes.shape({})),
    exclusions: PropTypes.shape({
      adgroupExclusions: PropTypes.shape({}),
    }),
  }).isRequired,
  onDataChange: PropTypes.func.isRequired,
  error: PropTypes.shape({
    serverErrors: PropTypes.arrayOf(PropTypes.shape({})),
    systemErrors: PropTypes.arrayOf(PropTypes.shape({})),
  }),
};

AdGroupEditSettingsStep.defaultProps = {
  error: null,
};
